import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { habitAPI, visionBoardAPI } from '../lib/supabase';
import HolographicCategoryChart from './HolographicCategoryChart';
import HolographicStreakChart from './HolographicStreakChart';
import HolographicVisionChart from './HolographicVisionChart';

const { FiActivity, FiTarget, FiTrendingUp, FiBarChart3, FiAward, FiZap, FiEye, FiArrowUp, FiHeart, FiBookOpen, FiCoffee, FiCpu, FiCalendar, FiClock, FiRefreshCw, FiChevronDown, FiAlertCircle, FiCheckCircle, FiXCircle } = FiIcons;

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [habits, setHabits] = useState([]);
  const [visions, setVisions] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [streakData, setStreakData] = useState([]);
  const [visionData, setVisionData] = useState([]);
  const [completionTrend, setCompletionTrend] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [timeRange, setTimeRange] = useState('30');
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [habitProgress, setHabitProgress] = useState({});
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Load data on initial render and when refreshKey changes
  useEffect(() => {
    loadData();
  }, [refreshKey, timeRange]);

  // Matrix-style canvas animation for background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Matrix characters
    const chars = 'アカサタナハマヤラワガザダバパイキシチニヒミリヰギジヂビピウクスツヌフムユルグズヅブプエケセテネヘメレヱゲゼデベペオコソトノホモヨロヲゴゾドボポヴッン0123456789ABCDEF';
    const columns = Math.floor(window.innerWidth / 20);
    const drops = [];

    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }

    const draw = () => {
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#0f0';
      ctx.font = '15px monospace';

      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(text, i * 20, drops[i] * 20);

        if (drops[i] * 20 > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const loadData = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }

    try {
      console.log('Analytics: Loading data...');

      // Load habits and visions in parallel
      const [habitsResult, visionsResult] = await Promise.all([
        habitAPI.getHabits(),
        visionBoardAPI.getVisionItems()
      ]);

      console.log('Analytics: Habits result:', habitsResult);
      console.log('Analytics: Visions result:', visionsResult);

      const habitsData = habitsResult.data || [];
      const visionsData = visionsResult.data || [];

      console.log('Analytics: Processed data - Habits:', habitsData.length, 'Visions:', visionsData.length);

      setHabits(habitsData);
      setVisions(visionsData);

      // Load progress data for habits
      const progressData = {};
      for (const habit of habitsData) {
        try {
          // Calculate date range based on selected time range
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - parseInt(timeRange));

          const formattedStartDate = startDate.toISOString().split('T')[0];
          const formattedEndDate = endDate.toISOString().split('T')[0];

          console.log(`Analytics: Loading progress for habit ${habit.id} from ${formattedStartDate} to ${formattedEndDate}`);

          const progressResult = await habitAPI.getProgress(
            habit.id,
            formattedStartDate,
            formattedEndDate
          );

          progressData[habit.id] = progressResult.data || [];
          console.log(`Analytics: Progress for habit ${habit.id}:`, progressData[habit.id].length, 'entries');
        } catch (error) {
          console.error(`Error loading progress for habit ${habit.id}:`, error);
          progressData[habit.id] = [];
        }
      }

      setHabitProgress(progressData);

      // Process data for charts
      console.log('Analytics: Processing chart data...');
      processHabitCategories(habitsData);
      processStreakData(habitsData);
      processVisionData(visionsData);
      generateCompletionTrend(habitsData, progressData);

      setLastRefresh(new Date());
      console.log('Analytics: Data loading complete');
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Process habit data by categories
  const processHabitCategories = (habitsData) => {
    console.log('Analytics: Processing habit categories for', habitsData.length, 'habits');

    if (habitsData.length === 0) {
      setCategoryData([]);
      return;
    }

    const categories = {};

    habitsData.forEach(habit => {
      const category = habit.category || 'other';
      if (!categories[category]) {
        categories[category] = {
          category: formatCategoryName(category),
          count: 0,
          total: 0,
          achieved: 0,
          achievementRate: 0,
          completion: ''
        };
      }

      categories[category].count++;
      categories[category].total++;

      // Check if habit is achieved (streak reached target)
      if (habit.streak >= habit.target_days) {
        categories[category].achieved++;
      }
    });

    // Calculate achievement rates and format for display
    Object.keys(categories).forEach(key => {
      const cat = categories[key];
      cat.achievementRate = cat.total > 0 ? Math.round((cat.achieved / cat.total) * 100) : 0;
      cat.completion = `${cat.achievementRate}%`;
    });

    // Convert to array for chart
    const chartData = Object.values(categories);
    console.log('Analytics: Category chart data:', chartData);
    setCategoryData(chartData);
  };

  // Process streak data for habits
  const processStreakData = (habitsData) => {
    console.log('Analytics: Processing streak data for', habitsData.length, 'habits');

    if (habitsData.length === 0) {
      setStreakData([]);
      return;
    }

    const streaks = habitsData
      .filter(habit => habit.streak !== undefined && habit.streak !== null) // Include all habits, even with 0 streaks
      .sort((a, b) => (b.streak || 0) - (a.streak || 0)) // Sort by streak (highest first)
      .slice(0, 6) // Take top 6 for display
      .map(habit => ({
        name: habit.title,
        streak: habit.streak || 0,
        target: habit.target_days || 30,
        completion: habit.target_days > 0 ? Math.min(100, Math.round(((habit.streak || 0) / habit.target_days) * 100)) : 0,
        icon: habit.icon || '🎯'
      }));

    console.log('Analytics: Streak chart data:', streaks);
    setStreakData(streaks);
  };

  // Process vision data by categories
  const processVisionData = (visionsData) => {
    console.log('Analytics: Processing vision data for', visionsData.length, 'visions');

    if (visionsData.length === 0) {
      setVisionData([]);
      return;
    }

    const categories = {};

    visionsData.forEach(vision => {
      const category = vision.category || 'other';
      if (!categories[category]) {
        categories[category] = {
          category: formatCategoryName(category),
          count: 0,
          total: 0,
          achieved: 0,
          achievementRate: 0
        };
      }

      categories[category].count++;
      categories[category].total++;

      if (vision.is_achieved) {
        categories[category].achieved++;
      }
    });

    // Calculate achievement rates
    Object.keys(categories).forEach(key => {
      const cat = categories[key];
      cat.achievementRate = cat.total > 0 ? Math.round((cat.achieved / cat.total) * 100) : 0;
    });

    // Convert to array for chart
    const chartData = Object.values(categories);
    console.log('Analytics: Vision chart data:', chartData);
    setVisionData(chartData);
  };

  // Generate completion trend data for line chart
  const generateCompletionTrend = (habitsData, progressData) => {
    console.log('Analytics: Generating completion trend');

    if (habitsData.length === 0) {
      setCompletionTrend([]);
      return;
    }

    // Get last 7 days
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }

    // Calculate completion rate for each day
    const trend = days.map(day => {
      let completed = 0;
      let total = 0;

      habitsData.forEach(habit => {
        total++;
        // Check if habit was completed on this day
        const habitProgressForDay = progressData[habit.id]?.find(p => p.date === day);
        if (habitProgressForDay?.completed) {
          completed++;
        }
      });

      const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        date: day,
        rate,
        day: new Date(day).toLocaleDateString('en-US', { weekday: 'short' })
      };
    });

    console.log('Analytics: Completion trend data:', trend);
    setCompletionTrend(trend);
  };

  // Helper to format category names for display
  const formatCategoryName = (category) => {
    if (!category) return 'Other';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Manual refresh handler
  const handleRefresh = () => {
    console.log('Analytics: Manual refresh triggered');
    setRefreshKey(prev => prev + 1);
  };

  // Calculate metrics from real data
  const calculateMetrics = () => {
    // Calculate total completions
    let totalCompletions = 0;
    let totalPossible = 0;

    Object.values(habitProgress).forEach(progressArray => {
      progressArray.forEach(progress => {
        totalPossible++;
        if (progress.completed) {
          totalCompletions++;
        }
      });
    });

    const completionRate = totalPossible > 0 ? Math.round((totalCompletions / totalPossible) * 100) : 0;

    // Find longest current streak
    const bestStreak = Math.max(...habits.map(h => h.streak || 0), 0);

    // Calculate perfect days (days where all habits were completed)
    const dayCompletions = {};
    Object.values(habitProgress).forEach(progressArray => {
      progressArray.forEach(progress => {
        if (!dayCompletions[progress.date]) {
          dayCompletions[progress.date] = { total: 0, completed: 0 };
        }
        dayCompletions[progress.date].total++;
        if (progress.completed) {
          dayCompletions[progress.date].completed++;
        }
      });
    });

    const perfectDays = Object.values(dayCompletions).filter(
      day => day.total > 0 && day.completed === day.total
    ).length;

    // Calculate vision achievement rate
    const visionAchievement = visions.length > 0 
      ? Math.round(visions.filter(v => v.is_achieved).length / visions.length * 100) 
      : 0;

    // Calculate total actions
    const totalActions = habits.length + visions.length;

    // Calculate total categories
    const totalCategories = new Set([
      ...habits.map(h => h.category),
      ...visions.map(v => v.category)
    ].filter(Boolean)).size;

    // Calculate average daily completion
    const dailyCompletions = Object.values(dayCompletions).map(day => 
      day.total > 0 ? (day.completed / day.total) * 100 : 0
    );
    const averageDaily = dailyCompletions.length > 0 
      ? Math.round(dailyCompletions.reduce((sum, rate) => sum + rate, 0) / dailyCompletions.length) 
      : 0;

    return {
      completionRate,
      bestStreak,
      averageDaily,
      perfectDays,
      visionAchievement,
      totalActions,
      totalCategories,
      totalCompletions,
      totalPossible
    };
  };

  // Calculate additional metrics for detailed stats
  const calculateDetailedMetrics = () => {
    // Find most consistent habit
    const habitConsistency = habits.map(habit => {
      const progress = habitProgress[habit.id] || [];
      const completionRate = progress.length > 0 
        ? (progress.filter(p => p.completed).length / progress.length) * 100 
        : 0;

      return {
        id: habit.id,
        title: habit.title,
        icon: habit.icon,
        completionRate: Math.round(completionRate)
      };
    });

    const mostConsistentHabit = [...habitConsistency].sort((a, b) => b.completionRate - a.completionRate)[0];

    // Find least consistent habit
    const leastConsistentHabit = [...habitConsistency]
      .filter(h => h.completionRate > 0) // Only consider habits with some completions
      .sort((a, b) => a.completionRate - b.completionRate)[0];

    // Find best day of week
    const dayOfWeekCompletions = {
      'Monday': { total: 0, completed: 0 },
      'Tuesday': { total: 0, completed: 0 },
      'Wednesday': { total: 0, completed: 0 },
      'Thursday': { total: 0, completed: 0 },
      'Friday': { total: 0, completed: 0 },
      'Saturday': { total: 0, completed: 0 },
      'Sunday': { total: 0, completed: 0 }
    };

    Object.values(habitProgress).forEach(progressArray => {
      progressArray.forEach(progress => {
        const date = new Date(progress.date);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        dayOfWeekCompletions[dayName].total++;
        if (progress.completed) {
          dayOfWeekCompletions[dayName].completed++;
        }
      });
    });

    const dayOfWeekRates = Object.entries(dayOfWeekCompletions).map(([day, stats]) => ({
      day,
      rate: stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0
    }));

    const bestDay = [...dayOfWeekRates].sort((a, b) => b.rate - a.rate)[0];

    // Calculate current streak for all habits
    const currentStreaks = habits.map(habit => ({
      id: habit.id,
      title: habit.title,
      icon: habit.icon,
      streak: habit.streak || 0
    }));

    return {
      mostConsistentHabit,
      leastConsistentHabit,
      bestDay,
      currentStreaks
    };
  };

  console.log('Analytics: Current state - Loading:', loading, 'Habits:', habits.length, 'Visions:', visions.length);

  if (loading) {
    return (
      <div className="min-h-screen bg-dominance-gradient flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-forge-400/30 border-t-forge-400 rounded-full animate-spin mb-4"></div>
          <h3 className="text-xl font-bold text-forge-400 mb-2">Loading Analytics</h3>
          <p className="text-forge-300">Processing your conquest data...</p>
        </motion.div>
      </div>
    );
  }

  const stats = calculateMetrics();
  const detailedStats = calculateDetailedMetrics();

  console.log('Analytics: Rendering with stats:', stats);

  return (
    <div className="min-h-screen bg-dominance-gradient relative">
      {/* Matrix background canvas */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none opacity-10"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Hero Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiActivity} className="text-2xl text-forge-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-forge-gradient bg-clip-text text-transparent">
              Dominance Analytics
            </h1>
          </div>
          <p className="text-xl text-forge-300 max-w-2xl mx-auto mb-4">
            Deep insights into your conquest patterns and victory metrics
          </p>

          {/* Debug Info */}
          <div className="text-xs text-forge-300 mb-4">
            Debug: {habits.length} habits, {visions.length} visions, {Object.keys(habitProgress).length} progress entries
          </div>

          {/* Refresh Button */}
          <div className="flex justify-center items-center mb-4">
            <button
              onClick={handleRefresh}
              className="flex items-center space-x-2 bg-forge-400/20 hover:bg-forge-400/30 text-forge-400 px-4 py-2 rounded-xl transition-all duration-300 border border-forge-400/30"
            >
              <SafeIcon icon={FiRefreshCw} className={refreshKey > 0 ? "animate-spin" : ""} />
              <span>Refresh Data</span>
            </button>
            <div className="text-xs text-forge-300 ml-3">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </div>
          </div>

          {/* Time Range Selector */}
          <div className="flex items-center justify-center space-x-2">
            <div className="bg-gradient-to-r from-shadow-900 to-shadow-800 rounded-2xl p-2 shadow-power border border-forge-400/20">
              {['7', '14', '30', '60', '90'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                    timeRange === range
                      ? 'bg-forge-gradient text-shadow-950 shadow-forge'
                      : 'text-forge-300 hover:text-forge-400 hover:bg-shadow-700/50'
                  }`}
                >
                  {range} days
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Habit Completion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="group relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl p-6 shadow-power border border-success-400/30 hover:shadow-dominance transition-all duration-500"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-success-400/10 to-success-600/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-success-400 to-success-600 rounded-2xl shadow-lg">
                  <SafeIcon icon={FiTarget} className="text-white text-xl" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-success-400">{stats.completionRate}%</div>
                  <div className="text-sm text-success-300">Habit Dominance</div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-success-300">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success-500 rounded-full"></div>
                  <span>Conquering daily</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Vision Achievement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="group relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl p-6 shadow-power border border-purple-400/30 hover:shadow-dominance transition-all duration-500"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-purple-600/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl shadow-lg">
                  <SafeIcon icon={FiEye} className="text-white text-xl" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-purple-400">{stats.visionAchievement}%</div>
                  <div className="text-sm text-purple-300">Vision Achievement</div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-purple-300">
                <SafeIcon icon={FiTrendingUp} className="text-purple-500" />
                <span>{visions.filter(v => v.is_achieved).length}/{visions.length} visions on track</span>
              </div>
            </div>
          </motion.div>

          {/* Action Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="group relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl p-6 shadow-power border border-blue-400/30 hover:shadow-dominance transition-all duration-500"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-blue-600/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl shadow-lg">
                  <SafeIcon icon={FiArrowUp} className="text-white text-xl" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-400">{stats.totalActions}</div>
                  <div className="text-sm text-blue-300">Action Steps</div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-300">
                <SafeIcon icon={FiActivity} className="text-blue-500" />
                <span>Total actions executed</span>
              </div>
            </div>
          </motion.div>

          {/* Best Streak */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="group relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl p-6 shadow-power border border-forge-400/30 hover:shadow-dominance transition-all duration-500"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-forge-400/10 to-forge-600/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-forge-gradient rounded-2xl shadow-lg">
                  <SafeIcon icon={FiZap} className="text-shadow-950 text-xl" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-forge-400">{stats.bestStreak}</div>
                  <div className="text-sm text-forge-300">Best Streak</div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-forge-300">
                <SafeIcon icon={FiAward} className="text-forge-500" />
                <span>Personal record</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Weekly Trend Chart */}
        {completionTrend.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-6 mb-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-forge-gradient rounded-2xl shadow-lg">
                <SafeIcon icon={FiBarChart3} className="text-shadow-950 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-forge-400">Weekly Completion Trend</h2>
                <p className="text-sm text-forge-300">Your habit completion rate over the last 7 days</p>
              </div>
            </div>

            <div className="h-64">
              <div className="flex h-48 items-end space-x-3 mb-4">
                {completionTrend.map((day, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-gradient-to-t from-forge-400 to-forge-500 rounded-t-sm"
                      style={{ height: `${day.rate}%` }}
                    ></div>
                    <div className="text-xs text-forge-300 mt-2">{day.day}</div>
                    <div className="text-sm font-bold text-forge-400">{day.rate}%</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center text-sm text-forge-300 mt-4 pt-4 border-t border-forge-400/20">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-forge-gradient rounded-full"></div>
                <span>Daily Completion Rate</span>
              </div>
              <div className="text-forge-400 font-bold">
                Average: {stats.averageDaily}%
              </div>
            </div>
          </motion.div>
        )}

        {/* Holographic Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Habit Categories Chart */}
          {categoryData.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <HolographicCategoryChart data={categoryData} title="Habit Categories" />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-forge-gradient rounded-2xl shadow-lg">
                  <SafeIcon icon={FiTarget} className="text-shadow-950 text-lg" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-forge-400">
                    Habit Categories
                  </h3>
                  <p className="text-forge-300 text-sm">
                    No data available
                  </p>
                </div>
              </div>
              <div className="text-center py-12">
                <p className="text-forge-400 mb-4">Create habits to see category distribution</p>
              </div>
            </motion.div>
          )}

          {/* Vision Categories Chart */}
          {visionData.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <HolographicVisionChart data={visionData} title="Vision Categories" />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl shadow-lg">
                  <SafeIcon icon={FiEye} className="text-white text-lg" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-purple-400">
                    Vision Categories
                  </h3>
                  <p className="text-purple-300 text-sm">
                    No vision data available
                  </p>
                </div>
              </div>
              <div className="text-center py-12">
                <p className="text-purple-300 mb-4">Create visions to see category distribution</p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Streak Performance Chart */}
        {streakData.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mb-8"
          >
            <HolographicStreakChart data={streakData} title="Streak Performance" />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-6 mb-8"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-forge-gradient rounded-2xl shadow-lg">
                <SafeIcon icon={FiZap} className="text-shadow-950 text-lg" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-forge-400">
                  Streak Performance
                </h3>
                <p className="text-forge-300 text-sm">
                  Awaiting data
                </p>
              </div>
            </div>
            <div className="text-center py-12">
              <p className="text-forge-400 mb-4">Build habit streaks to see performance metrics</p>
            </div>
          </motion.div>
        )}

        {/* No data message if completely empty */}
        {habits.length === 0 && visions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-8 text-center"
          >
            <div className="w-20 h-20 bg-forge-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-power">
              <SafeIcon icon={FiActivity} className="text-shadow-950 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-forge-400 mb-4">No Analytics Data Yet</h3>
            <p className="text-forge-300 mb-8 max-w-lg mx-auto">
              Create habits and visions to start tracking your progress and see powerful analytics insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-forge-gradient text-shadow-950 rounded-xl font-semibold hover:shadow-power transition-all duration-300">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiTarget} />
                  <span>Create Habit</span>
                </div>
              </button>
              <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiEye} />
                  <span>Add Vision</span>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Analytics;